import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Link,
  Select,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaAngleRight, FaPlus, FaTrash } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import InputField from "../../../components/InputField";
import SelectField from "../../../components/SelectField";
import SearchableSelect from "../../../components/SearchableSelect";
import { incotermOptions } from "../../../utils/incoterms";
import { getCompanies } from "../../../services/companyService";
import { getUsers } from "../../../services/userService";
import { getResponsibleOptions } from "../../../utils/users";
import {
  getLastRef,
  getProcessById,
  importItems,
  saveProcess,
} from "../../../services/processService";
import NcmAnalysisModal from "./components/NcmAnalysisModal";
import { NcmAnalysisItem } from "../../../models/Process";
import { warnValidation } from "../../../services/utils";
import { DateTime } from "luxon";
import { getPurchaseOrder } from "../../../services/purchaseOrderService";
import { PurchaseOrderViewModel } from "../../../models/PurchaseOrder";
import ProcessItem from "./components/processItem";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import {
  ProcessItemViewModel,
  ProcessViewModel,
} from "../../../models/Process";
import { countries } from "../../../models/countries";
import { companyOptions, useCompany } from "../../../contexts/CompanyContext";
import { CvfRef, processStatus } from "../../Configurations/tabs/types";
import Layout from "../../../components/Layout";
import DocumentUpload from "../../../components/DocumentUpload";
import ConfirmModal from "./components/confirmModal";
import ScanDocumentModal, { ScanFieldKey } from "./components/ScanDocumentModal";
import { getExchangeConditions } from "../../../services/exchangeConditionService";
import {
  ExchangeCondition,
  referenceDateLabel,
} from "../../../models/ExchangeCondition";
import CreateExchangeConditionModal from "../../Configurations/tabs/CreateExchangeConditionModal";
import {
  ProcessPartner,
  ProcessPartnerType,
} from "../../../models/ProcessPartner";
import { getProcessPartners } from "../../../services/processPartnerService";

// Shape of each dynamic parcela row
interface Parcela {
  id?: string;
  valorPagamento?: string;
  dataPagamento?: string;
  taxaCambio?: string;
  dataCambio?: string;
  exchangeConditionId?: string;
  dueDate?: string;
  paidAt?: string;
  paymentStatus?: "paid" | "unpaid";
  forecastExchangeRate?: string;
  forecastExchangeRateDate?: string;
  documentsSentForExchange?: boolean;
  clearDueDate?: boolean;
  clearPaidAt?: boolean;
  clearPaymentStatus?: boolean;
  clearForecastExchangeRate?: boolean;
  clearForecastExchangeRateDate?: boolean;
  clearDocumentsSentForExchange?: boolean;
}

const emptyParcela = (): Parcela => ({
  valorPagamento: "",
  dataPagamento: "",
  taxaCambio: "",
  dataCambio: "",
  exchangeConditionId: "",
  paymentStatus: undefined,
  documentsSentForExchange: undefined,
});

const ordinals = [
  "Primeira",
  "Segunda",
  "Terceira",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sétima",
  "Oitava",
  "Nona",
  "Décima",
];

const SaveProcess = () => {
  const [firstValueStatus, setFirstValueStatus] = useState("");
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("China");
  const [currency, setCurrency] = useState("USD");
  const [error, setError] = useState<string>();
  const [items, setItems] = useState<ProcessItemViewModel[]>([
    {
      ncm: "",
      quantity: undefined,
      product: "",
      unitValue: "",
    },
  ]);

  // Dynamic parcelas replacing the fixed primeira/segunda blocks
  const [parcelas, setParcelas] = useState<Parcela[]>([emptyParcela()]);
  const [removedFinancialItemIds, setRemovedFinancialItemIds] = useState<string[]>([]);

  const auth = useAuthUser<any>();

  const [file, setFile] = useState<File>();
  const [ciente, setCiente] = useState(false);
  const inputRef = useRef(null);
  const [ncmAnalysis, setNcmAnalysis] = useState<NcmAnalysisItem[]>([]);
  const [isNcmModalOpen, setIsNcmModalOpen] = useState(false);

  const [clientRef, setClientRef] = useState<{
    id: string;
    name: string;
    cnpj: string;
  }>();
  const [client, setClient] = useState<{
    id: string;
    name: string;
    cnpj: string;
  }>();
  const [process, setProcess] = useState<ProcessViewModel>();
  const [isUrgent, setIsUrgent] = useState(false);
  const [latestRef, setLatestRef] = useState<CvfRef>();
  const [responsible, setResponsible] = useState<{ id: string; name: string }>();
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);

  // Armador (customsAgent) e Agente de cargas (carrier) saíram do unform: viraram
  // selects alimentados pelo cadastro. Continuam gravando o NOME em texto, e aceitam
  // valor livre para não perder o que já estava digitado nos processos antigos.
  const [shipowners, setShipowners] = useState<ProcessPartner[]>([]);
  const [freightAgents, setFreightAgents] = useState<ProcessPartner[]>([]);
  const [customsAgent, setCustomsAgent] = useState<string>();
  const [carrier, setCarrier] = useState<string>();

  const startMigration = async () => {
    setLoadingSave(true);
    if (file) {
      try {
        const response = await importItems(file, process?.id ?? "");
        if (response) {
          await get();
          setFile(undefined);
          toast({
            title: "Processo salvo.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          // Show NCM analysis if the API returned any
          const analyses: NcmAnalysisItem[] =
            response.data?.ncmAnalysis ?? [];
          if (analyses.length > 0) {
            setNcmAnalysis(analyses);
            setIsNcmModalOpen(true);
          }
        }
      } catch (error: any) {
        const responseData = error?.response?.data;
        const detail = typeof responseData === "string"
          ? responseData.split("\n")[0]
          : responseData?.message || error?.message;
        toast({
          title: "Erro ao importar itens.",
          description: detail,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
    setLoadingSave(false);
  };

  const fetchData = useCallback(async () => {
    try {
      const data = await getLastRef();
      setLatestRef(data);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
    }, 10000);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchData]);

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");
  // Criação a partir de uma ordem de compra (/saveProcess?fromPo=<id>): pré-preenche e vincula ao salvar.
  const fromPo = !id ? new URLSearchParams(location.search).get("fromPo") : null;
  const [sourcePo, setSourcePo] = useState<PurchaseOrderViewModel>();

  // Espelha PurchaseOrderProcessSync (backend) — mesma tabela usada na propagação de edições da PO.
  const loadFromPurchaseOrder = async (poId: string) => {
    const po: PurchaseOrderViewModel | undefined = await getPurchaseOrder(poId);
    if (!po) return;
    if (po.process?.cvfRef) {
      toast({
        title: `A PO ${po.poNumber} já está vinculada ao processo ${po.process.cvfRef}.`,
        status: "warning",
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    const isoDate = (v?: string) => {
      if (!v) return undefined;
      const d = DateTime.fromISO(v);
      return d.isValid && d.year > 1900 ? d.toFormat("yyyy-LL-dd") : undefined;
    };
    const money = (v?: number) =>
      v && v > 0
        ? v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : undefined;
    const upper = (v?: string) => v?.trim().toUpperCase() || undefined;

    // Cliente da PO: casa pelo CNPJ (só dígitos — pode estar salvo com ou sem máscara). Resolvido aqui,
    // antes do form montar: o SearchableSelect só lê o valor inicial na montagem.
    const companies: { id: string; name: string; cnpj: string }[] = (await getCompanies(true)) ?? [];
    const digits = (v?: string) => (v ?? "").replace(/\D/g, "");
    const target = digits(po.cnpj);
    const match = target
      ? companies.find((c) => digits(c.cnpj) === target)
      : companies.find((c) => c.name === po.client);
    if (match) setClient(match);

    setSourcePo(po);
    setCurrency("USD");
    setProcess({
      exporter: upper(po.supplier),
      invoice: upper(po.invoiceRef),
      dischargePort: upper(po.destinationPort),
      incoterm: upper(po.incoterm),
      containerQuantity: po.containerQty?.trim() || undefined,
      totalInvoice: money(po.totalFobValue),
      internationalFreight: money(po.freightAllIn),
      shippingDate: isoDate(po.shipmentDate),
      arrivalDate: isoDate(po.arrivalDate),
      orderOpeningDate: isoDate(po.documentDate),
      openingDate: DateTime.now().toFormat("yyyy-LL-dd"),
    } as any);
    setItems([
      { ncm: po.productNcm?.trim() ?? "", quantity: undefined, product: "", unitValue: "" },
    ]);
  };

  const get = async () => {
    setRemovedFinancialItemIds([]);
    if (id) {
      const response = await getProcessById(id);
      if (response) {
        setFirstValueStatus(response.status);
        setIsUrgent(Boolean(response.isUrgent));
        const newCurrency = !response.totalInvoice
          ? null
          : response.totalInvoice[0] === "€"
            ? "EUR"
            : response.totalInvoice[0] === "$"
              ? "USD"
              : response.totalInvoice?.split(" ")[0] === "null"
                ? "USD"
                : response.totalInvoice?.split(" ").length > 1
                  ? response.totalInvoice?.split(" ")[0]
                  : "USD";
        setCurrency(newCurrency);

        setProcess({
          ...response,
          statusDate: DateTime.fromISO(response.statusDate).toFormat(
            "yyyy-LL-dd",
          ),
          openingDate: DateTime.fromISO(response.openingDate).toFormat(
            "yyyy-LL-dd",
          ),
          shippingDate: DateTime.fromISO(response.shippingDate).toFormat(
            "yyyy-LL-dd",
          ),
          arrivalDate: DateTime.fromISO(response.arrivalDate).toFormat(
            "yyyy-LL-dd",
          ),
          validity:
            (response.validity?.length ?? 0) === 0
              ? undefined
              : (response.validity?.split("/")[0].length > 2
                  ? DateTime.fromISO(response.validity)
                  : DateTime.fromFormat(
                      response.validity,
                      response.validity?.split("/")[0].length === 1
                        ? "L/dd/yyyy"
                        : "LL/dd/yyyy",
                    )
                ).toFormat("yyyy-LL-dd"),
          registrationDate: response.registrationDate
            ? DateTime.fromISO(response.registrationDate).toFormat("yyyy-LL-dd")
            : "",
          customsClearanceDate: response.customsClearanceDate
            ? DateTime.fromISO(response.customsClearanceDate).toFormat(
                "yyyy-LL-dd",
              )
            : "",
          nfIssued: response.nfIssued
            ? DateTime.fromISO(response.nfIssued).toFormat("yyyy-LL-dd")
            : "",
          totalInvoice: response.totalInvoice?.replace(newCurrency + " ", ""),
        });

        setCountry(response.countryOfOrigin);
        setCustomsAgent(response.customsAgent || undefined);
        setCarrier(response.carrier || undefined);
        setItems(
          response.items.map((i: any) => ({
            ...i,
            unitValue: i.unitValue?.replace(newCurrency + " ", ""),
            netWeight: i.netWeight != null ? String(i.netWeight).replace(".", ",") : "",
          })),
        );
        setClient({
          id: response.clientId,
          name: response.clientName,
          cnpj: response.clientCnpj,
        });
        if (response.responsibleId) {
          setResponsible({ id: response.responsibleId, name: response.responsibleName ?? "" });
        }
        setClientRef({
          id: response.refId,
          name: response.refName,
          cnpj: response.refCnpj,
        });

        // Map ALL financialItems into parcelas array, sorted by intId
        const sorted = [...(response.financialItems ?? [])].sort(
          (a: any, b: any) => a.intId - b.intId,
        );
        if (sorted.length > 0) {
          setParcelas(
            sorted.map((f: any) => ({
              id: f.id,
              valorPagamento:
                f.valorPagamento === "#VALUE!"
                  ? ""
                  : (f.valorPagamento?.replace(newCurrency + " ", "") ?? ""),
              dataPagamento: f.dataPagamento
                ? DateTime.fromISO(f.dataPagamento).toFormat("yyyy-LL-dd")
                : "",
              taxaCambio:
                f.taxaCambio === "#VALUE!" ? "" : (f.taxaCambio ?? ""),
              dataCambio: f.dataCambio
                ? DateTime.fromISO(f.dataCambio).toFormat("yyyy-LL-dd")
                : "",
              exchangeConditionId: f.exchangeConditionId ?? "",
              dueDate: f.dueDate ? DateTime.fromISO(f.dueDate).toFormat("yyyy-LL-dd") : "",
              paidAt: f.paidAt ? DateTime.fromISO(f.paidAt).toFormat("yyyy-LL-dd") : "",
              paymentStatus: f.paymentStatus ?? undefined,
              forecastExchangeRate: f.forecastExchangeRate?.toString() ?? "",
              forecastExchangeRateDate: f.forecastExchangeRateDate ? DateTime.fromISO(f.forecastExchangeRateDate).toFormat("yyyy-LL-dd") : "",
              documentsSentForExchange: f.documentsSentForExchange,
            })),
          );
        }
      }
    } else if (fromPo) {
      await loadFromPurchaseOrder(fromPo);
    }
    setLoading(false);
  };

  useEffect(() => {
    get();
  }, [id]);

  const [clients, setClients] = useState<
    { id: string; name: string; cnpj: string }[]
  >([]);
  const navigate = useNavigate();
  const toast = useToast();

  const [conditions, setConditions] = useState<ExchangeCondition[]>([]);
  // "Ler documento (IA)": selects (armador/país) só leem o default na montagem → key força remount.
  const [scanKey, setScanKey] = useState(0);
  const {
    isOpen: isScanOpen,
    onOpen: onScanOpen,
    onClose: onScanClose,
  } = useDisclosure();

  const scanCurrent = (key: ScanFieldKey): string => {
    if (key === "customsAgent") return customsAgent ?? "";
    if (key === "countryOfOrigin") return country ?? "";
    const v = formRef.current?.getFieldValue(key);
    if (v instanceof Date) return DateTime.fromJSDate(v).toFormat("yyyy-MM-dd");
    return v ? String(v) : "";
  };

  const scanNormalize = (key: ScanFieldKey, value: string): string | null => {
    if (key === "countryOfOrigin") {
      const match = countries.portuguese.find(
        (c) => c.name.toLowerCase() === value.trim().toLowerCase()
      );
      return match ? match.name : null;
    }
    if (key === "incoterm") {
      return incotermOptions.some((o) => o.value === value) ? value : null;
    }
    return value;
  };

  const applyScan = (values: Partial<Record<ScanFieldKey, string>>) => {
    Object.entries(values).forEach(([key, value]) => {
      if (!value) return;
      if (key === "customsAgent") setCustomsAgent(value);
      else if (key === "countryOfOrigin") setCountry(value);
      else formRef.current?.setFieldValue(key, value);
    });
    if (values.customsAgent || values.countryOfOrigin) setScanKey((k) => k + 1);
    onScanClose();
    toast({
      title: "Valores aplicados ao formulário",
      description: "Revise e clique em salvar para gravar o processo.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };
  const [conditionParcelaIndex, setConditionParcelaIndex] = useState<
    number | null
  >(null);
  const {
    isOpen: isCondOpen,
    onOpen: onCondOpen,
    onClose: onCondClose,
  } = useDisclosure();

  const loadConditions = async () => {
    const list = await getExchangeConditions();
    setConditions(list ?? []);
  };

  useEffect(() => {
    loadConditions();
  }, []);

  // Calcula a Data do Câmbio de uma parcela a partir de uma data do processo.
  const applyCondition = (index: number, condition?: ExchangeCondition) => {
    if (!condition) return;
    const baseValue = formRef.current?.getFieldValue(condition.referenceDate);
    const baseDate = baseValue ? DateTime.fromISO(baseValue) : undefined;
    if (!baseValue || !baseDate?.isValid) {
      toast({
        title: `Preencha a ${referenceDateLabel(
          condition.referenceDate,
        )} do processo primeiro.`,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const offset =
      condition.direction === "Antes" ? -condition.days : condition.days;
    const result = baseDate.plus({ days: offset }).toFormat("yyyy-LL-dd");
    updateParcela(index, "dataCambio", result);
    toast({
      title: `Data do câmbio preenchida (${result
        .split("-")
        .reverse()
        .join("/")}).`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  useEffect(() => {
    const getClients = async () => {
      const response = await getCompanies(true);
      if (response) setClients(response);
    };
    getClients();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      const data = await getUsers();
      setUsers(getResponsibleOptions(data, auth?.company));
    };
    loadUsers();
  }, [auth?.company]);

  useEffect(() => {
    const loadPartners = async () => {
      const [owners, agents] = await Promise.all([
        getProcessPartners(ProcessPartnerType.Shipowner),
        getProcessPartners(ProcessPartnerType.FreightAgent),
      ]);
      setShipowners(owners ?? []);
      setFreightAgents(agents ?? []);
    };
    loadPartners();
  }, []);

  const { currentCompany } = useCompany();

  // Helpers to update a single field on a parcela row
  const updateParcela = (
    index: number,
    field: keyof Parcela,
    value: string | boolean | undefined,
  ) => {
    setParcelas((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    );
  };

  const addParcela = () => setParcelas((prev) => [...prev, emptyParcela()]);

  const removeParcela = (index: number) => {
    const removedId = parcelas[index]?.id;
    if (removedId) setRemovedFinancialItemIds((prev) => Array.from(new Set([...prev, removedId])));
    setParcelas((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (rawData: any) => {
    formRef.current?.setErrors({});
    // UX only — backend (Process.NormalizeStrings) is the source of truth for uppercase.
    const data: any = Object.fromEntries(
      Object.entries(rawData).map(([k, v]) => [
        k,
        typeof v === "string" ? v.toUpperCase() : v,
      ]),
    );
    const type =
      auth?.company !== "Geral"
        ? companyOptions.find((c) => c.label === auth.company)?.value
        : data.type;
    const manualDateFields = [
      "orderOpeningDate",
      "expectedReadinessDate",
      "expectedClientDeliveryDate",
      "actualReadinessDate",
      "collectionDate",
      "actualShippingDate",
      "actualArrivalDate",
      "actualClientDeliveryDate",
    ];
    const manualDates = Object.fromEntries(manualDateFields.map((field) => [
      field,
      data[field]?.length > 0 ? `${data[field]}T00:00:00` : undefined,
    ]));
    const clearedManualDates = Object.fromEntries(manualDateFields.map((field) => [
      `clear${field[0].toUpperCase()}${field.slice(1)}`,
      data[field] === "",
    ]));
    try {
      const body = {
        id,
        ...data,
        ...manualDates,
        ...clearedManualDates,
        type: type.length == 0 ? undefined : type,
        refId: (clientRef?.id?.length ?? 0) > 0 ? clientRef?.id : undefined,
        statusDate: data.statusDate.length > 0 ? data.statusDate : undefined,
        shippingDate:
          data.shippingDate.length > 0 ? data.shippingDate : undefined,
        registrationDate:
          data.registrationDate?.length > 0 ? data.registrationDate : undefined,
        validity: data.validity?.length > 0 ? data.validity : undefined,
        arrivalDate:
          data.arrivalDate?.length > 0 ? data.arrivalDate : undefined,
        customsClearanceDate:
          data.customsClearanceDate?.length > 0
            ? data.customsClearanceDate
            : undefined,
        nfIssued: data.nfIssued?.length > 0 ? data.nfIssued : undefined,
        cifValue:
          data.cifValue === "" || data.cifValue == null
            ? undefined
            : Number(data.cifValue),
        countryOfOrigin: country,
        items: items.map((i) => ({
          ...i,
          unitValue: `${currency} ${i.unitValue}`,
          netWeight: parseNetWeight(i.netWeight),
        })),
        totalInvoice: `${currency} ${data.totalInvoice}`,
        clientId: client?.id,
        responsibleId: responsible?.id,
        isUrgent,
        removedFinancialItemIds,
        // Vêm dos selects (fora do unform), então precisam sobrescrever o spread acima.
        customsAgent: customsAgent?.toUpperCase() || undefined,
        carrier: carrier?.toUpperCase() || undefined,
        // Campos da PO sem input no formulário + vínculo PO → processo (feito no backend ao criar).
        ...(sourcePo && {
          purchaseOrderId: sourcePo.id,
          invoiceDate:
            sourcePo.supplierInvoiceDate && DateTime.fromISO(sourcePo.supplierInvoiceDate).year > 1900
              ? sourcePo.supplierInvoiceDate
              : undefined,
          freeTime: sourcePo.freeTimeDays ? String(sourcePo.freeTimeDays) : undefined,
        }),
        // Map dynamic parcelas back to financialItems with sequential intId
        financialItems: parcelas.map((p, index) => ({
          id: p.id,
          intId: index + 1,
          valorPagamento: !p.valorPagamento
            ? undefined
            : p.valorPagamento.includes(currency)
              ? p.valorPagamento
              : `${currency} ${p.valorPagamento}`,
          dataPagamento:
            (p.dataPagamento?.length ?? 0) > 0 ? p.dataPagamento : undefined,
          taxaCambio: p.taxaCambio || undefined,
          dataCambio:
            (p.dataCambio?.length ?? 0) > 0 ? p.dataCambio : undefined,
          exchangeConditionId: p.exchangeConditionId || undefined,
          dueDate: p.dueDate || undefined,
          paidAt: p.paymentStatus === "paid" ? p.paidAt || undefined : undefined,
          paymentStatus: p.paymentStatus,
          forecastExchangeRate: p.forecastExchangeRate ? Number(p.forecastExchangeRate.replace(",", ".")) : undefined,
          forecastExchangeRateDate: p.forecastExchangeRateDate || undefined,
          documentsSentForExchange: p.documentsSentForExchange,
          clearDueDate: p.clearDueDate,
          clearPaidAt: p.clearPaidAt,
          clearPaymentStatus: p.clearPaymentStatus,
          clearForecastExchangeRate: p.clearForecastExchangeRate,
          clearForecastExchangeRateDate: p.clearForecastExchangeRateDate,
          clearDocumentsSentForExchange: p.clearDocumentsSentForExchange,
        })),
      };

      setLoadingSave(true);
      const response = await saveProcess(body);
      if (response?.data.success) {
        toast({
          title: "Processo salvo.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate(sourcePo && response.data.id ? `/process?id=${response.data.id}` : "/processes");
      } else {
        toast({
          title: "Erro ao salvar processo.",
          description: response?.data.notifications[0],
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      const responseData = err?.response?.data;
      toast({
        title: "Erro ao salvar processo.",
        description:
          responseData?.notifications?.[0] ??
          responseData?.message ??
          err?.message ??
          "Tente novamente.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
      warnValidation(err, formRef.current);
    } finally {
      setLoadingSave(false);
    }
  };
  function saveCheck() {
    setError(undefined);
    const type =
      auth?.company !== "Geral"
        ? companyOptions.find((c) => c.label === auth?.company)?.value
        : formRef.current?.getFieldValue("type");

    if (!client?.id) {
      toast({
        title: "Selecione o cliente do processo.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (type === undefined || type === null || String(type).length === 0) {
      toast({
        title: "Selecione a empresa do processo.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (
      items.length === 0 ||
      items.some(
        (i) =>
          (i.product?.length ?? 0) === 0 ||
          (i.quantity?.length ?? 0) === 0 ||
          (i.unitValue?.length ?? 0) === 0,
      )
    ) {
      setError("É necessário criar os items do processo");
      return;
    }
    const novoStatus = formRef.current?.getFieldValue("status");
    if (novoStatus === "ENCERRADO" && novoStatus !== firstValueStatus) {
      setIsOpenConfirm(true);
    } else {
      formRef.current?.submitForm();
    }
  }
  return (
    <Box height="100%">
      <Layout>
        <Flex
          pb="64px"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          w="100%"
          backgroundColor="var(--main-bg-color)"
        >
          <Flex
            alignItems="center"
            mb="24px"
            pt="0"
            gap="8px"
            mt={{ base: "16px", md: "27px" }}
            width="90%"
          >
            <Link
              color="var(--icon-color)"
              onClick={() => navigate("/processes")}
              size="md"
            >
              Processos
            </Link>
            <FaAngleRight />
            {id && (
              <>
                <Link
                  color="var(--icon-color)"
                  onClick={() => navigate(`/process?id=${id}`)}
                  size="md"
                >
                  Detalhe do processo
                </Link>
                <FaAngleRight />
              </>
            )}
            <Text size="md">{id ? "Editar" : "Criar"} processo</Text>
          </Flex>
          <Flex width={{ base: "95%", md: "65%" }}>
            <Heading size="lg" mb="48px" fontWeight="normal">
              {id ? "Editar" : "Criar"} processo
            </Heading>
          </Flex>
          {sourcePo && (
            <Flex
              width={{ base: "95%", md: "65%" }}
              mt="-32px"
              mb="24px"
              p="12px 16px"
              borderRadius="10px"
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              fontSize="sm"
              direction="column"
            >
              <Text fontWeight="600">
                Criando a partir da PO {sourcePo.poNumber} — {sourcePo.client}
              </Text>
              <Text color="gray.600">
                Dados de embarque, fornecedor e valores vieram da PO. Complete os itens e salve: a PO fica
                vinculada a este processo e alterações futuras na PO serão refletidas aqui.
              </Text>
            </Flex>
          )}
          {loading ? (
            <Flex h="100vh" w="100vw">
              <Spinner />
            </Flex>
          ) : (
            <Form
              ref={formRef}
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                alignItems: "center",
              }}
              initialData={
                process ? process : { openingDate: DateTime.now().toJSDate() }
              }
            >
              <ScanDocumentModal
                isOpen={isScanOpen}
                onClose={onScanClose}
                getCurrent={scanCurrent}
                normalize={scanNormalize}
                onApply={applyScan}
              />
              <Box width={{ base: "95%", md: "65%" }}>
                <Flex
                  backgroundColor="white"
                  flexDirection="column"
                  padding={{ base: "24px", md: "32px" }}
                >
                  <Flex justify="flex-end" mb="12px">
                    <Button
                      variant="outline"
                      borderRadius="10px"
                      onClick={onScanOpen}
                    >
                      Ler documento (IA)
                    </Button>
                  </Flex>
                  <FormControl>
                    <Flex
                      align="center"
                      justify="space-between"
                      p="16px"
                      bg={isUrgent ? "red.50" : "gray.50"}
                      border="1px solid"
                      borderColor={isUrgent ? "red.300" : "gray.200"}
                      borderRadius="md"
                    >
                      <Box>
                        <FormLabel mb="0">Processo urgente</FormLabel>
                        <Text fontSize="sm" color="gray.600">
                          O destaque será exibido nas listas, no detalhe e no Financeiro.
                        </Text>
                      </Box>
                      <Switch
                        colorScheme="red"
                        size="lg"
                        isChecked={isUrgent}
                        onChange={(event) => setIsUrgent(event.target.checked)}
                        aria-label="Processo urgente"
                      />
                    </Flex>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Modo de Importação: </FormLabel>
                      <SelectField
                        name="importMode"
                        placeholder="Modo de Importação"
                        options={[
                          { label: "Encomenda", value: "Encomenda" },
                          { label: "Distribuição", value: "Distribuição" },
                          { label: "Conta e Ordem", value: "Conta e Ordem" },
                        ]}
                      />
                    </Box>
                  </FormControl>
                  {auth?.company === "Geral" && (
                    <FormControl isRequired>
                      <Box w="100%" mt={10}>
                        <FormLabel>Empresa do processo: </FormLabel>
                        <SelectField
                          isReadOnly={!!id}
                          name="type"
                          placeholder="Empresa do processo"
                          options={companyOptions}
                        />
                      </Box>
                    </FormControl>
                  )}
                  {/* ── Client ── */}
                  <FormControl isRequired>
                    <Box mt={10}>
                      <FormLabel>Cliente: </FormLabel>
                      <SearchableSelect
                        defaultSelectValue={{
                          value: client?.id,
                          label: client?.name,
                          subLabel: client?.cnpj,
                        }}
                        options={clients.map((c) => ({
                          value: c.id,
                          label: c.name,
                          subLabel: c.cnpj,
                        }))}
                        name="clientId"
                        value={client?.id}
                        setValue={(value) => {
                          const selClient = clients.find(
                            (c) => c.id.toLowerCase() === value?.toLowerCase(),
                          );
                          setClient(selClient);
                        }}
                      />
                      {client?.cnpj && (
                        <Text mt="12px">CNPJ: {client.cnpj}</Text>
                      )}
                    </Box>
                  </FormControl>

                  <Heading size="md" fontWeight="normal" mt="64px">
                    Adicionar ref de cliente
                  </Heading>
                  <Text mt="8px">
                    Serve para o processo permanecer vinculado a CVF, mas
                    aparecer para o cliente
                  </Text>
                  <Box>
                    <SearchableSelect
                      defaultSelectValue={{
                        value: clientRef?.id,
                        label: clientRef?.name,
                        subLabel: clientRef?.cnpj,
                      }}
                      options={clients.map((c) => ({
                        value: c.id,
                        label: c.name,
                        subLabel: c.cnpj,
                      }))}
                      name="refId"
                      value={clientRef?.id}
                      setValue={(value) => {
                        const selClient = clients.find(
                          (c) => c.id.toLowerCase() === value?.toLowerCase(),
                        );
                        setClientRef(selClient);
                      }}
                    />
                    {clientRef?.cnpj && (
                      <Text mt="12px">CNPJ: {clientRef.cnpj}</Text>
                    )}
                  </Box>

                  {/* ── Responsible user ── */}
                  <FormControl>
                    <Box mt={10}>
                      <FormLabel>Responsável: </FormLabel>
                      <SearchableSelect
                        defaultSelectValue={
                          responsible
                            ? { value: responsible.id, label: responsible.name }
                            : undefined
                        }
                        options={users.map((u) => ({
                          value: u.id,
                          label: u.name,
                        }))}
                        name="responsibleId"
                        value={responsible?.id}
                        setValue={(value) => {
                          const sel = users.find(
                            (u) => u.id.toLowerCase() === value?.toLowerCase()
                          );
                          setResponsible(sel);
                        }}
                      />
                    </Box>
                  </FormControl>

                  {/* ── Dates & process fields ── */}
                  {process?.openingDate && (
                    <FormControl>
                      <Box w="100%" mt={10}>
                        <FormLabel>Data de abertura: </FormLabel>
                        <InputField
                          type="date"
                          name="openingDate"
                          placeholder="Data de Abertura"
                          isReadOnly
                        />
                      </Box>
                    </FormControl>
                  )}

                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data de embarque: </FormLabel>
                      <InputField
                        type="date"
                        name="shippingDate"
                        placeholder="Data de embarque"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data de chegada: </FormLabel>
                      <InputField
                        type="date"
                        name="arrivalDate"
                        placeholder="Data de chegada"
                      />
                    </Box>
                  </FormControl>

                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>CVF Ref (nº do processo): </FormLabel>
                      <Text>
                        Se não for preenchido, será criado um novo número na
                        sequencia conforme o tipo selecionado (CVF ou Alfa)
                      </Text>
                      <InputField
                        isReadOnly={!!id}
                        name="cvfRef"
                        placeholder="CVF Ref."
                        type="number"
                      />
                      Último processo salvo no sistema:{" "}
                      <Heading size="sm" fontWeight="normal">
                        {currentCompany === 1 ? ( // "CVF Logística":
                          latestRef?.logistics
                        ) : currentCompany === 2 ? ( // "CVF Distribuidora":
                          latestRef?.distributions
                        ) : currentCompany === 3 ? ( // "CVF Rondônia":
                          latestRef?.rondonia
                        ) : currentCompany === 4 ? ( // "W Trading":
                          latestRef?.wtrading
                        ) : (
                          <Box>
                            <Heading size="sm" fontWeight="normal">
                              Logística: {latestRef?.logistics}
                            </Heading>
                            <Heading size="sm" fontWeight="normal">
                              Distribuidora: {latestRef?.distributions}
                            </Heading>
                            {(latestRef?.rondonia ?? 0) > 0 && (
                              <Heading size="sm" fontWeight="normal">
                                Rondônia: {latestRef?.rondonia}
                              </Heading>
                            )}
                            {(latestRef?.wtrading ?? 0) > 0 && (
                              <Heading size="sm" fontWeight="normal">
                                WTrading: {latestRef?.wtrading}
                              </Heading>
                            )}
                          </Box>
                        )}
                      </Heading>
                    </Box>
                  </FormControl>

                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Sub-Ref CVF: </FormLabel>
                      <Text>
                        Sufixo do processo desmembrado (ex.: 1501 + A = 1501A).
                        Deixe em branco se não houver.
                      </Text>
                      <InputField name="cvfSubRef" placeholder="Sub-Ref." />
                    </Box>
                  </FormControl>

                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Ref. do cliente: </FormLabel>
                      <InputField
                        name="clientRef"
                        placeholder="Ref. do cliente"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Armador: </FormLabel>
                      <SearchableSelect
                        key={`customsAgent-${process?.id ?? "new"}-${scanKey}`}
                        placeholder="Armador"
                        isCreatable
                        isClearable
                        defaultSelectValue={
                          customsAgent
                            ? { value: customsAgent, label: customsAgent }
                            : undefined
                        }
                        options={shipowners.map((s) => ({
                          value: s.name,
                          label: s.name,
                        }))}
                        setValue={(value) => setCustomsAgent(value)}
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Exportador: </FormLabel>
                      <InputField name="exporter" placeholder="Exportador" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Produto: </FormLabel>
                      <InputField name="product" placeholder="Produto" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Nº do contêiner: </FormLabel>
                      <InputField
                        name="containerNumber"
                        placeholder="Nº do contêiner"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Total Invoice: </FormLabel>
                      <Flex>
                        <Select
                          w="100px"
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                        >
                          <option value="USD">US$</option>
                          <option value="EUR">€</option>
                          <option value="BRL">R$</option>
                        </Select>
                        <InputField
                          mask="currency"
                          name="totalInvoice"
                          placeholder="Total invoice"
                        />
                      </Flex>
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Porto de Descarga: </FormLabel>
                      <InputField
                        name="dischargePort"
                        placeholder="Porto de Descarga"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Frete internacional: </FormLabel>
                      <Flex align="center">
                        <Text mr={2} fontWeight="bold">
                          US$
                        </Text>
                        <InputField
                          mask="currency"
                          name="internationalFreight"
                          placeholder="Frete internacional"
                        />
                      </Flex>
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Taxa do frete: </FormLabel>
                      <InputField
                        name="freightTax"
                        placeholder="Taxa do frete"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Incoterm: </FormLabel>
                      <SelectField
                        w="100%"
                        name="incoterm"
                        placeholder="Incoterm"
                        options={incotermOptions}
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>País de Origem: </FormLabel>
                      <SearchableSelect
                        key={`country-${scanKey}`}
                        defaultSelectValue={{ label: country, value: country }}
                        options={countries.portuguese.map((c) => ({
                          value: c.name,
                          label: c.name,
                        }))}
                        value={country}
                        setValue={(value) => setCountry(value)}
                        name="countryOfOrigin"
                        placeholder="País de Origem"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Total de Contêineres: </FormLabel>
                      <InputField
                        name="containerQuantity"
                        placeholder="Total de Contêineres"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Fatura: </FormLabel>
                      <InputField name="invoice" placeholder="Fatura" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>LI: </FormLabel>
                      <InputField name="li" placeholder="LI" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Validade: </FormLabel>
                      <InputField
                        type="date"
                        name="validity"
                        placeholder="Validade"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>BL/AWB: </FormLabel>
                      <InputField name="blAwbCrt" placeholder="BL/AWB" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Despachante: </FormLabel>
                      <InputField name="dispatcher" placeholder="Despachante" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Embarcação: </FormLabel>
                      <InputField name="vessel" placeholder="Embarcação" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Cotação Internacional: </FormLabel>
                      <InputField
                        name="internationalQuote"
                        placeholder="Cotação Internacional"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Cotação Rodoviária: </FormLabel>
                      <InputField
                        name="roadQuote"
                        placeholder="Cotação Rodoviária"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Seguro: </FormLabel>
                      <InputField name="insurance" placeholder="Seguro" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>ICMS: </FormLabel>
                      <InputField name="icms" placeholder="ICMS" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Numerário: </FormLabel>
                      <InputField name="cashAmount" placeholder="Numerário" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Documentos: </FormLabel>
                      <InputField name="documents" placeholder="Documentos" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Catálogo PU: </FormLabel>
                      <InputField name="catalogPu" placeholder="Catálogo PU" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Status LI: </FormLabel>
                      <InputField name="liStatus" placeholder="Status LI" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Segmento: </FormLabel>
                      <InputField name="segment" placeholder="Segmento" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Canal: </FormLabel>
                      <SelectField
                        name="channel"
                        placeholder="Canal"
                        options={[
                          { label: "Verde", value: "VERDE" },
                          { label: "Amarelo", value: "AMARELO" },
                          { label: "Vermelho", value: "VERMELHO" },
                          { label: "Cinza", value: "CINZA" },
                        ]}
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Vistoria: </FormLabel>
                      <InputField name="inspection" placeholder="Vistoria" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Observação do canal: </FormLabel>
                      <InputField
                        name="channelObservation"
                        placeholder="Observação do canal"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>DI: </FormLabel>
                      <InputField name="di" placeholder="DI" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data Registro: </FormLabel>
                      <InputField
                        type="date"
                        name="registrationDate"
                        placeholder="Data Registro"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data do Desembaraço: </FormLabel>
                      <InputField
                        type="date"
                        name="customsClearanceDate"
                        placeholder="Data do Desembaraço"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Agente de Cargas: </FormLabel>
                      <SearchableSelect
                        key={`carrier-${process?.id ?? "new"}`}
                        placeholder="Agente de Cargas"
                        isCreatable
                        isClearable
                        defaultSelectValue={
                          carrier ? { value: carrier, label: carrier } : undefined
                        }
                        options={freightAgents.map((a) => ({
                          value: a.name,
                          label: a.name,
                        }))}
                        setValue={(value) => setCarrier(value)}
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Demurrage: </FormLabel>
                      <InputField name="demurrage" placeholder="Demurrage" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>CE Mercante: </FormLabel>
                      <InputField name="ceMerchant" placeholder="CE Mercante" />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data Emissão da NF: </FormLabel>
                      <InputField
                        type="date"
                        name="nfIssued"
                        placeholder="Data Emissão da NF"
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Status: </FormLabel>
                      <SelectField
                        name="status"
                        placeholder="Status"
                        options={processStatus}
                      />
                    </Box>
                  </FormControl>
                  <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Data do status: </FormLabel>
                      <InputField
                        type="date"
                        name="statusDate"
                        placeholder="Data do status"
                      />
                    </Box>
                  </FormControl>

                  {/* ── Process items ── */}
                  <FormLabel mt="32px" position="relative">
                    Itens do processo:
                  </FormLabel>
                  <Flex gap="32px" flexDir="column">
                    {items.map((i, index) => (
                      <ProcessItem
                        key={index}
                        {...{ error, setError, index, i, items, setItems }}
                        clientCnpj={client?.cnpj}
                        getExporter={() => String(formRef.current?.getFieldValue("exporter") ?? "")}
                        country={country ?? ""}
                      />
                    ))}
                  </Flex>
                  {error && (
                    <Text color="red.300">{error}</Text>
                  )}
                  <Button
                    w={{ base: "100%", md: "30%" }}
                    mt="32px"
                    onClick={() => {
                      setItems((items) => [
                        ...items,
                        {
                          ncm: "",
                          quantity: undefined,
                          product: "",
                          unitValue: "",
                        },
                      ]);
                      setError(undefined);
                    }}
                    backgroundColor="var(--icon-color)"
                    color="white"
                  >
                    <Flex gap="8px" alignItems="center">
                      <FaPlus /> Adicionar item
                    </Flex>
                  </Button>

                  {/* ── AI import ── */}
                  {process && (
                    <>
                      <FormLabel mt="64px" position="relative">
                        Adicionar itens com Inteligência Artificial (Gera um
                        custo):
                      </FormLabel>
                      <Text mb="24px">Deve ser um arquivo PDF</Text>
                      <DocumentUpload
                        inputRef={inputRef}
                        file={file}
                        setFile={setFile}
                        accept="application/pdf"
                        onFileChange={(e) => setFile(e)}
                        w="100%"
                        py="20px"
                      />
                      <Checkbox
                        marginTop="16px"
                        isChecked={ciente}
                        onChange={(e) => setCiente(e.target.checked)}
                      >
                        Estou ciente de que isso pode gerar um custo.
                      </Checkbox>
                      <Button
                        isLoading={loadingSave}
                        isDisabled={!file || !ciente}
                        marginTop="16px"
                        backgroundColor="var(--icon-color)"
                        color="white"
                        w={{ base: "100%", md: "15%" }}
                        borderRadius="3px"
                        onClick={() => startMigration()}
                      >
                        Importar
                      </Button>
                    </>
                  )}

                  <Heading size="md" fontWeight="normal" mt="64px" mb="8px">
                    Dados manuais para os relatórios financeiros
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb="20px">
                    Estes valores não são inferidos de pedidos de compra, documentos ou cotações externas.
                  </Text>
                  <Flex wrap="wrap" gap="16px">
                    {[
                      ["otherReferences", "Outras referências", "text"],
                      ["operationalObservation", "Observação operacional / etapa", "text"],
                      ["transportMode", "Via de transporte", "text"],
                      ["exporterCode", "Código do exportador", "text"],
                      ["duimpVersion", "Versão DUIMP", "text"],
                      ["duimpKey", "Chave DUIMP", "text"],
                      ["protocolNumber", "Nº do protocolo", "text"],
                      ["cifValue", "Valor CIF", "number"],
                      ["cifCurrency", "Moeda CIF", "text"],
                      ["orderOpeningDate", "Abertura do pedido", "date"],
                      ["expectedReadinessDate", "Prontidão prevista", "date"],
                      ["expectedClientDeliveryDate", "Entrega prevista ao cliente", "date"],
                      ["actualReadinessDate", "Prontidão efetiva", "date"],
                      ["collectionDate", "Coleta", "date"],
                      ["actualShippingDate", "Embarque confirmado", "date"],
                      ["actualArrivalDate", "Chegada confirmada", "date"],
                      ["actualClientDeliveryDate", "Entrega efetiva ao cliente", "date"],
                    ].map(([name, label, type]) => (
                      <FormControl key={name} flex="1 1 280px">
                        <FormLabel>{label}:</FormLabel>
                        <InputField name={name} type={type} placeholder={label} />
                      </FormControl>
                    ))}
                  </Flex>

                  {/* ══════════════════════════════════════
                      Financeiro — dynamic parcelas
                  ══════════════════════════════════════ */}
                  <Heading size="md" fontWeight="normal" mt="64px">
                    Financeiro
                  </Heading>
                  {/* <FormControl>
                    <Box w="100%" mt={10}>
                      <FormLabel>Condição de Pagamento:</FormLabel>
                      <InputField
                        w="100%"
                        name="paymentCondition"
                        placeholder="Condição"
                      />
                    </Box>
                  </FormControl> */}

                  {parcelas.map((parcela, index) => (
                    <Box
                      key={index}
                      mt="32px"
                      p="24px"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      position="relative"
                    >
                      {/* Header row with label + remove button */}
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        mb="4px"
                      >
                        <Heading size="md" fontWeight="normal">
                          {index < ordinals.length
                            ? `${ordinals[index]} Parcela`
                            : `Parcela ${index + 1}`}
                        </Heading>
                        {parcelas.length > 1 && (
                          <IconButton
                            aria-label="Remover parcela"
                            icon={<FaTrash />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeParcela(index)}
                          />
                        )}
                      </Flex>

                      <FormControl>
                        <Box w="100%" mt={6}>
                          <FormLabel>Valor do Pagamento: </FormLabel>
                          <InputField
                            mask="currency"
                            // Use a unique name so unform doesn't collide
                            name={`parcela_valorPagamento_${index}`}
                            placeholder="Valor do Pagamento"
                            value={parcela.valorPagamento}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateParcela(
                                index,
                                "valorPagamento",
                                e.target.value,
                              )
                            }
                          />
                        </Box>
                      </FormControl>

                      <Flex gap="16px" flexDir={{ base: "column", md: "row" }} mt={6}>
                        <FormControl>
                          <FormLabel>Vencimento manual:</FormLabel>
                          <InputField type="date" name={`parcela_dueDate_${index}`} value={parcela.dueDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { updateParcela(index, "dueDate", e.target.value); updateParcela(index, "clearDueDate", e.target.value === ""); }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Status financeiro manual:</FormLabel>
                          <Select value={parcela.paymentStatus ?? ""}
                            onChange={(e) => { const status = e.target.value as "" | "paid" | "unpaid"; updateParcela(index, "paymentStatus", status || undefined); updateParcela(index, "clearPaymentStatus", status === ""); }}>
                            <option value="">Não informado (regra legada)</option>
                            <option value="unpaid">Não pago</option>
                            <option value="paid">Pago</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Pago em:</FormLabel>
                          <InputField type="date" name={`parcela_paidAt_${index}`} value={parcela.paidAt}
                            disabled={parcela.paymentStatus !== "paid"}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { updateParcela(index, "paidAt", e.target.value); updateParcela(index, "clearPaidAt", e.target.value === ""); }} />
                        </FormControl>
                      </Flex>

                      <Flex gap="16px" flexDir={{ base: "column", md: "row" }} mt={6}>
                        <FormControl>
                          <FormLabel>Cotação de previsão (manual):</FormLabel>
                          <InputField name={`parcela_forecastExchangeRate_${index}`} value={parcela.forecastExchangeRate}
                            placeholder="Ex.: 5,4321"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { updateParcela(index, "forecastExchangeRate", e.target.value); updateParcela(index, "clearForecastExchangeRate", e.target.value === ""); }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Data da cotação prevista:</FormLabel>
                          <InputField type="date" name={`parcela_forecastExchangeRateDate_${index}`} value={parcela.forecastExchangeRateDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { updateParcela(index, "forecastExchangeRateDate", e.target.value); updateParcela(index, "clearForecastExchangeRateDate", e.target.value === ""); }} />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Docs enviados para câmbio:</FormLabel>
                          <Select value={parcela.documentsSentForExchange == null ? "" : String(parcela.documentsSentForExchange)}
                            onChange={(e) => { const value = e.target.value; updateParcela(index, "documentsSentForExchange", value === "" ? undefined : value === "true"); updateParcela(index, "clearDocumentsSentForExchange", value === ""); }}>
                            <option value="">Desconhecido</option>
                            <option value="true">Sim</option>
                            <option value="false">Não</option>
                          </Select>
                        </FormControl>
                      </Flex>

                      <FormControl>
                        <Box w="100%" mt={6}>
                          <FormLabel>Data do Pagamento: </FormLabel>
                          <InputField
                            type="date"
                            name={`parcela_dataPagamento_${index}`}
                            placeholder="Data do Pagamento"
                            value={parcela.dataPagamento}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateParcela(
                                index,
                                "dataPagamento",
                                e.target.value,
                              )
                            }
                          />
                        </Box>
                      </FormControl>

                      <FormControl>
                        <Box w="100%" mt={6}>
                          <FormLabel>Taxa de Câmbio: </FormLabel>
                          <InputField
                            name={`parcela_taxaCambio_${index}`}
                            placeholder="Taxa de Câmbio"
                            value={parcela.taxaCambio}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateParcela(index, "taxaCambio", e.target.value)
                            }
                          />
                        </Box>
                      </FormControl>

                      <FormControl>
                        <Box w="100%" mt={6}>
                          <FormLabel>Data do Câmbio: </FormLabel>
                          <InputField
                            type="date"
                            name={`parcela_dataCambio_${index}`}
                            placeholder="Data do Câmbio"
                            value={parcela.dataCambio}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateParcela(index, "dataCambio", e.target.value)
                            }
                          />
                        </Box>
                      </FormControl>

                      {/* Condição de câmbio por parcela (preenche a Data do Câmbio) */}
                      <FormControl>
                        <Box w="100%" mt={6}>
                          <FormLabel>
                            Condição de câmbio (preenche a Data do Câmbio):
                          </FormLabel>
                          <Text fontSize="sm" color="gray.600" mb="8px">
                            Opcional. Calcula a Data do Câmbio a partir de uma
                            data do processo (ex.: embarque + 50 dias). Deixe
                            vazio para preencher manual (ex.: antecipação).
                          </Text>
                          <Flex
                            gap="8px"
                            flexDir={{ base: "column", md: "row" }}
                          >
                            <Select
                              placeholder="Selecione uma condição"
                              value={parcela.exchangeConditionId ?? ""}
                              onChange={(e) => {
                                const condId = e.target.value;
                                updateParcela(
                                  index,
                                  "exchangeConditionId",
                                  condId,
                                );
                                applyCondition(
                                  index,
                                  conditions.find((c) => c.id === condId),
                                );
                              }}
                            >
                              {conditions.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </Select>
                            <Button
                              minW="130px"
                              isDisabled={!parcela.exchangeConditionId}
                              onClick={() =>
                                applyCondition(
                                  index,
                                  conditions.find(
                                    (c) =>
                                      c.id === parcela.exchangeConditionId,
                                  ),
                                )
                              }
                            >
                              Recalcular
                            </Button>
                            <Button
                              minW="130px"
                              backgroundColor="var(--icon-color)"
                              color="white"
                              onClick={() => {
                                setConditionParcelaIndex(index);
                                onCondOpen();
                              }}
                            >
                              Criar nova
                            </Button>
                          </Flex>
                        </Box>
                      </FormControl>
                    </Box>
                  ))}

                  <Button
                    w={{ base: "100%", md: "30%" }}
                    mt="24px"
                    onClick={addParcela}
                    backgroundColor="var(--icon-color)"
                    color="white"
                  >
                    <Flex gap="8px" alignItems="center">
                      <FaPlus /> Adicionar parcela
                    </Flex>
                  </Button>
                </Flex>
              </Box>

              <Flex w={{ base: "95%", md: "65%" }} justifyContent="flex-end">
                <Button
                  isLoading={loadingSave}
                  backgroundColor="var(--icon-color)"
                  type="button"
                  onClick={saveCheck}
                  color="white"
                  mb="64px"
                  my="32px"
                >
                  Salvar
                </Button>
                <ConfirmModal
                  isOpen={isOpenConfirm}
                  onClose={() => setIsOpenConfirm(false)}
                  onConfirm={() => {
                    setIsOpenConfirm(false);
                    formRef.current?.submitForm();
                  }}
                />
              </Flex>
            </Form>
          )}
          <CreateExchangeConditionModal
            isOpen={isCondOpen}
            onClose={onCondClose}
            onSaved={async (newId) => {
              const list = await getExchangeConditions();
              setConditions(list ?? []);
              if (newId && conditionParcelaIndex !== null) {
                updateParcela(
                  conditionParcelaIndex,
                  "exchangeConditionId",
                  newId,
                );
                applyCondition(
                  conditionParcelaIndex,
                  (list ?? []).find((c) => c.id === newId),
                );
              }
            }}
          />
        </Flex>
      </Layout>

      <NcmAnalysisModal
        isOpen={isNcmModalOpen}
        onClose={() => setIsNcmModalOpen(false)}
        analyses={ncmAnalysis}
      />
    </Box>
  );
};

export default SaveProcess;


/** Peso pt-BR ("1.250,5" ou "1250,5") -> número; vazio/inválido -> undefined. */
function parseNetWeight(v?: number | string | null): number | undefined {
  if (v == null || v === "") return undefined;
  if (typeof v === "number") return v;
  const n = Number(v.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
